import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, TypographyColors } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations, useCurrentUser } from '@/hooks';
// Receive IS the shipment form, so it uses that slice's component rather than a copy.
import { ShipmentForm, MODES } from '@/slices/incoming-shipments/components/ShipmentForm';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetContractForReceiveShipmentQuery,
  useReceiveShipmentForContractMutation,
} from '../api/contractsApi';

// Inbounds ▸ Contracts ▸ Receive shipment.
export const ReceiveShipmentPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, contractId } = useParams();
  const user = useCurrentUser();

  // GraphQL: account-scoped, so unlike the mutation it needs no `module`.
  const { data: contract, isLoading, isError } = useGetContractForReceiveShipmentQuery(contractId);
  const [receiveShipmentForContract, { isLoading: isSubmitting }] =
    useReceiveShipmentForContractMutation();

  const goToContract = useCallback(() => {
    navigate(ROUTES.contractDetail(module, contractId));
  }, [navigate, module, contractId]);

  const goToContracts = useCallback(() => {
    navigate(ROUTES.contracts(module));
  }, [navigate, module]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        await receiveShipmentForContract({ module, id: contractId, values }).unwrap();
        goToContract();
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [receiveShipmentForContract, module, contractId, goToContract],
  );

  // <Form> seeds initialValues once, on mount, so it waits for the contract.
  const initialValues = useMemo(() => contract?.values, [contract]);

  // <TerminalField> narrows these by the facility the contract put in form state.
  const formOptions = useMemo(() => ({ sites: user?.sites ?? EMPTY_LIST }), [user]);

  const summaryFields = useMemo(
    () =>
      contract
        ? [
            { label: t('FIELD_CONTRACT_ID'), value: contract.contractId },
            { label: t('FIELD_MATERIAL_NAME'), value: contract.materialName },
            { label: t('FORM_RECEIVED_AT_FACILITY'), value: contract.facilityName },
            // The route always creates a single-site shipment, whatever the contract says.
            { label: t('FORM_MULTI_SITE_TRANSFER'), value: t('COMMON_NO') },
            { label: t('FORM_SENT_BY_SUPPLIER'), value: contract.supplierName },
            {
              label: t('RECEIVE_SHIPMENT_TYPE'),
              value: t('RECEIVE_SHIPMENT_TYPE_TO_RECEIVE'),
              color: TypographyColors.warning,
            },
          ]
        : EMPTY_LIST,
    [t, contract],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !contract) {
    return (
      <EmptyState
        title={t('RECEIVE_SHIPMENT_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToContracts}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <ShipmentForm
      mode={MODES.receive}
      title={t('ACTION_RECEIVE_SHIPMENT')}
      submitLabel={t('ACTION_RECEIVE_SHIPMENT')}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onClose={goToContract}
      isSubmitting={isSubmitting}
      summaryFields={summaryFields}
      material={contract.material}
      terminalLocked={contract.terminalLocked}
      options={formOptions}
    />
  );
};
